import { useUpdateScimGroupMapping } from '@tinct/core-api';
import { ReadScimGroupMapping } from '@tinct/core-api-models';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';

export type MapGroupToAdminModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  group: ReadScimGroupMapping;
};

const membersOf = (count: number) =>
  count === 1 ? '1 member' : `${count} members`;

/**
 * Confirms mapping a directory group to Admin, which the API only accepts with `confirmAdmin`: every
 * member of the group, today's and tomorrow's, becomes an admin of the workspace.
 */
export const MapGroupToAdminModal = ({
  open,
  onOpenChange,
  organizationId,
  group,
}: MapGroupToAdminModalProps) => {
  const updateMapping = useUpdateScimGroupMapping({
    onSuccess: () => {
      toast.success(`${group.displayName ?? 'The group'} now maps to Admin`);
      onOpenChange(false);
    },
  });

  // The API's refusal (e.g. 409, 5704) stays in the dialog rather than closing on a toast
  const refusal = updateMapping.error?.error ?? null;
  const isMapping = updateMapping.isPending;
  const memberCount = group.memberCount ?? 0;

  return (
    // Held open while the mapping is applied, so that its outcome is what the admin sees
    <Dialog open={open} onOpenChange={(next) => (next || !isMapping) && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Map {group.displayName ?? 'this group'} to Admin?</DialogTitle>
          <DialogDescription>
            The {membersOf(memberCount)} of this group become admins of the
            workspace: full access except billing, security settings included.
            Anyone your directory adds to the group later becomes an admin too.
          </DialogDescription>
        </DialogHeader>

        {refusal && (
          <Alert variant="important">
            <TriangleAlertIcon />
            <AlertDescription>{refusal}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isMapping}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() =>
              group.groupId &&
              updateMapping.mutate({
                organizationId,
                groupId: group.groupId,
                role: 'admin',
                confirmAdmin: true,
              })
            }
            isLoading={isMapping}
          >
            Map to Admin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
